import { type IndexUiState } from 'instantsearch.js';

import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import { type EsIndex } from '../types';

import {
	BettingInsightsCard,
	type BettingInsightsCardConfig,
} from './BettingInsightsCard';
import {
	BettingInsightsRefinements,
	type BettingInsightsRefinementsConfig,
} from './BettingInsightsRefinements';
import {
	BettingInsightsResults,
	type BettingInsightsResultsConfig,
} from './BettingInsightsResults';
import {
	BettingInsightsSearch,
	type BettingInsightsSearchConfig,
} from './BettingInsightsSearch';
import {
	BettingInsightsSorting,
	type BettingInsightsSortingConfig,
} from './BettingInsightsSorting';

export interface StyleSheet {
	[key: string]: string;
}

export interface BettingInsightsUIConfig<TIndex extends EsIndex> {
	card: BettingInsightsCardConfig<TIndex>;
	initialUiState?: (this: BettingInsightsUI<TIndex>) => IndexUiState;
	refinements: BettingInsightsRefinementsConfig<TIndex>;
	results: BettingInsightsResultsConfig<TIndex>;
	search?: BettingInsightsSearchConfig<TIndex>;
	sorting: BettingInsightsSortingConfig<TIndex>;
}

export class BettingInsightsUI<TIndex extends EsIndex>
	extends BettingInsightsComponent
	implements BettingInsightsUIConfig<TIndex>
{
	card: BettingInsightsCard<TIndex>;
	initialUiState?: () => IndexUiState;
	refinements: BettingInsightsRefinements<TIndex>;
	results: BettingInsightsResults<TIndex>;
	search?: BettingInsightsSearch<TIndex>;
	sorting: BettingInsightsSorting<TIndex>;

	constructor({
		card,
		initialUiState,
		refinements,
		results,
		search,
		sorting,
		...requestContext
	}: BettingInsightsUIConfig<TIndex> & RequestContext) {
		super(requestContext);
		this.card = BettingInsightsCard.withRequestContext<TIndex>(
			requestContext,
			card,
		);
		this.initialUiState = initialUiState?.bind(this);
		this.refinements = BettingInsightsRefinements.withRequestContext<TIndex>(
			requestContext,
			refinements,
		);
		this.results = BettingInsightsResults.withRequestContext<TIndex>(
			requestContext,
			results,
		);
		this.search = search
			? BettingInsightsSearch.withRequestContext(requestContext, search)
			: undefined;
		this.sorting = BettingInsightsSorting.withRequestContext<TIndex>(
			requestContext,
			sorting,
		);
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsUIConfig<TIndex>,
	) {
		return new this<TIndex>({
			...requestContext,
			...config,
		});
	}
}
